import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PaymentFormProps {
  /** Total amount in display format (e.g., "25.00") */
  totalAmount: string;
  /** Callback when payment succeeds, receives paymentIntentId */
  onPaymentSuccess: (paymentIntentId: string) => void;
  /** Callback when payment fails */
  onPaymentError: (error: string) => void;
  /** Callback when user attempts payment (for analytics) */
  onPaymentAttempted?: () => void;
  /** Whether the form is disabled (e.g., during booking creation) */
  disabled?: boolean;
}

/**
 * Stripe PaymentElement form for collecting card details.
 * Must be rendered inside a Stripe Elements provider with a clientSecret.
 */
export function PaymentForm({
  totalAmount,
  onPaymentSuccess,
  onPaymentError,
  onPaymentAttempted,
  disabled = false,
}: PaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet
      return;
    }

    // Track payment attempt
    onPaymentAttempted?.();

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Don't redirect - we'll handle the result ourselves
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        // Show error to customer
        setErrorMessage(error.message || "An unexpected error occurred.");
        onPaymentError(error.message || "Payment failed");
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // Payment succeeded
        onPaymentSuccess(paymentIntent.id);
      } else if (paymentIntent && paymentIntent.status === "requires_action") {
        // Additional authentication required (3D Secure, etc.)
        // Stripe handles this automatically with redirect: "if_required"
        setErrorMessage("Additional authentication required. Please complete the verification.");
      } else {
        setErrorMessage("Payment could not be processed. Please try again.");
        onPaymentError("Payment status: " + (paymentIntent?.status || "unknown"));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "An unexpected error occurred";
      setErrorMessage(message);
      onPaymentError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: "tabs",
        }}
      />

      {errorMessage && (
        <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md" role="alert">
          {errorMessage}
        </div>
      )}

      <Button
        type="submit"
        className="w-full"
        size="lg"
        disabled={!stripe || !elements || isProcessing || disabled}
        data-testid="pay-button"
      >
        {isProcessing ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {t("payment.processing")}
          </>
        ) : (
          t("payment.payButton", { amount: totalAmount })
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        {t("payment.securedByStripe")}
      </p>
    </form>
  );
}

export default PaymentForm;
